// v1
// Path: frontend/src/components/QuickBooksSync/IntegrationSettingsModal.jsx
// Inputs: token, onClose, onSettingsUpdated
// Outputs: React JSX modal allowing configuration of entity-to-root-table defaults and QuickBooks destinations
// Summary: Environmental settings interface managing default Passport ERP root table names and target QuickBooks entities stored in the database.

import React, { useState, useEffect } from 'react';

export const IntegrationSettingsModal = ({ token, onClose, onSettingsUpdated }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [defaults, setDefaults] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState({
        entityType: '',
        defaultRootTable: '',
        defaultQBEntity: 'Customer',
        description: '',
        isActive: true
    });
    const [message, setMessage] = useState(null);

    const qbEntities = [
        'Customer', 'Vendor', 'Item', 'Account', 'Invoice', 'Bill', 'Payment', 'JournalEntry'
    ];

    const fetchDefaults = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/quickbooks/settings/entity-defaults', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDefaults(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('[IntegrationSettingsModal] Error loading defaults:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDefaults();
    }, [token]);

    const handleEdit = (item) => {
        setEditingItem(item);
        setForm({
            entityType: item.EntityType,
            defaultRootTable: item.DefaultRootTable,
            defaultQBEntity: item.DefaultQBEntity,
            description: item.Description || '',
            isActive: !!item.IsActive
        });
    };

    const handleNew = () => {
        setEditingItem('NEW');
        setForm({
            entityType: '',
            defaultRootTable: '',
            defaultQBEntity: 'Customer',
            description: '',
            isActive: true
        });
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
        setMessage(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            const res = await fetch('/api/quickbooks/settings/entity-defaults', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: data.message || 'Saved successfully!' });
                setEditingItem(null);
                await fetchDefaults();
                if (onSettingsUpdated) onSettingsUpdated();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to save entity default.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-850">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                            <span className="material-symbols-outlined text-[24px]">tune</span>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white tracking-wide">
                                Integration Environmental Settings
                            </h3>
                            <p className="text-xs text-slate-400">
                                Configure database-driven Passport root table names, QB destinations, and company partition rules.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Explanation Banner */}
                <div className="px-6 py-3 bg-blue-950/40 border-b border-blue-900/40 text-xs text-blue-200 flex items-start gap-2.5">
                    <span className="material-symbols-outlined text-[18px] text-blue-400 shrink-0 mt-0.5">info</span>
                    <div>
                        <strong>Passport Company Suffix Convention:</strong> The last 2 digits of the selected database name (e.g. <code>pbsdata00</code> &rarr; <code>00</code>) are automatically appended to the Root Table name (e.g. <code>CUSFIL</code> + <code>00</code> = <code>CUSFIL00</code>). Table definitions here are stored dynamically in <code>Sync.EntityTableDefaults</code>.
                    </div>
                </div>

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {message && (
                        <div className={`p-3 rounded-lg text-xs border flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300' : 'bg-red-950/60 border-red-800 text-red-300'}`}>
                            <span className="material-symbols-outlined text-[16px]">
                                {message.type === 'success' ? 'check_circle' : 'error'}
                            </span>
                            <span>{message.text}</span>
                        </div>
                    )}

                    {/* Table of Defaults */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                Registered Entity & Root Table Defaults ({defaults.length})
                            </h4>
                            {!editingItem && (
                                <button
                                    onClick={handleNew}
                                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[14px]">add</span>
                                    + Add Entity Default
                                </button>
                            )}
                        </div>

                        {loading ? (
                            <div className="text-center py-10 text-slate-400 text-xs">
                                <span className="material-symbols-outlined animate-spin text-2xl mb-1">sync</span>
                                <p>Loading entity defaults...</p>
                            </div>
                        ) : defaults.length === 0 ? (
                            <div className="text-center py-8 bg-slate-800/40 rounded-xl border border-slate-700 text-slate-400 text-xs">
                                No entity defaults configured. Click "+ Add Entity Default" above.
                            </div>
                        ) : (
                            <div className="border border-slate-700/80 rounded-xl overflow-hidden bg-slate-800/60">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-slate-850 text-slate-400 border-b border-slate-700">
                                            <th className="p-3 font-semibold">Entity Type</th>
                                            <th className="p-3 font-semibold">Root Table</th>
                                            <th className="p-3 font-semibold">QB Destination</th>
                                            <th className="p-3 font-semibold">Example Table (00)</th>
                                            <th className="p-3 font-semibold">Description</th>
                                            <th className="p-3 font-semibold text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700/60">
                                        {defaults.map(item => (
                                            <tr key={item.EntityDefaultID} className="hover:bg-slate-750 transition-colors">
                                                <td className="p-3 font-bold text-white flex items-center gap-1.5">
                                                    <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                                    {item.EntityType}
                                                </td>
                                                <td className="p-3 font-mono text-emerald-300 font-bold">
                                                    {item.DefaultRootTable}
                                                </td>
                                                <td className="p-3 font-mono text-blue-300">
                                                    {item.DefaultQBEntity}
                                                </td>
                                                <td className="p-3 font-mono text-slate-400">
                                                    {item.DefaultRootTable}00
                                                </td>
                                                <td className="p-3 text-slate-300 max-w-xs truncate">
                                                    {item.Description || '-'}
                                                </td>
                                                <td className="p-3 text-right">
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        className="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs font-medium transition-colors"
                                                    >
                                                        Edit
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Edit / New Form */}
                    {editingItem && (
                        <form onSubmit={handleSave} className="bg-slate-850 border border-blue-500/40 rounded-xl p-5 space-y-4 shadow-lg">
                            <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                                <h4 className="text-sm font-bold text-blue-400 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">edit_note</span>
                                    {editingItem === 'NEW' ? 'Create New Entity Default' : `Edit Entity: ${form.entityType}`}
                                </h4>
                                <button type="button" onClick={handleCancelEdit} className="text-slate-400 hover:text-white text-xs">
                                    Cancel
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                <div>
                                    <label className="block text-slate-400 mb-1 font-semibold">Entity Type</label>
                                    <input
                                        type="text"
                                        required
                                        disabled={editingItem !== 'NEW'}
                                        value={form.entityType}
                                        onChange={e => setForm({ ...form, entityType: e.target.value })}
                                        placeholder="e.g. Customers, Items, Orders"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white disabled:opacity-50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 mb-1 font-semibold">Default Root Table (No Suffix)</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.defaultRootTable}
                                        onChange={e => setForm({ ...form, defaultRootTable: e.target.value.toUpperCase() })}
                                        placeholder="e.g. CUSFIL, ITMFIL, CHARTF"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white font-mono uppercase"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 mb-1 font-semibold">Target QuickBooks Destination</label>
                                    <select
                                        value={form.defaultQBEntity}
                                        onChange={e => setForm({ ...form, defaultQBEntity: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                    >
                                        {qbEntities.map(ent => (
                                            <option key={ent} value={ent}>{ent}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="text-xs">
                                <label className="block text-slate-400 mb-1 font-semibold">Functional Description / Notes</label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                    placeholder="e.g. Passport Order Entry Order Header (OEHDRxx)"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="px-3.5 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm"
                                >
                                    {saving ? (
                                        <>
                                            <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-[14px]">save</span>
                                            Save Entity Default
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-slate-800 bg-slate-850 flex justify-between items-center text-xs text-slate-400">
                    <div>
                        Stored in SQL database table <code>Sync.EntityTableDefaults</code>.
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg font-semibold transition-colors border border-slate-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
